import React, { FC, memo, useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useButton, useCurrency, useI18n, useLogger } from '~Hooks';
import Icon from '~ComponentsImproved/Icon';
import infoIcon from '../../../../../Assets/icons/info.svg';
import ItemImage from '../../../Components/ItemImage';
import RadioButton from '../../../Components/RadioButton';
import QuantityStepper from '../../../Components/QuantityStepper';
import { EposMenuItem } from '../../../types';
import useSessionActive from '../../../Hooks/useSessionActive';
import UpsellInfoModal from './UpsellInfoModal';

/** About the widest a card gets in the 3-up grid. */
const CARD_IMAGE_WIDTH = 320;

/**
 * Image-first upsell card. The photo sells the add-on; the price sits as a
 * "+ 39 kr" delta so it reads as a small step, not a new purchase. The whole
 * card is the tap target - the radio/stepper only shows the state.
 */
const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 20px;
  overflow: hidden;
  cursor: pointer;
  user-select: none;
  color: var(--eposText);
  background: var(--eposSurface);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.2s ease-out;

  &.is-touching {
    transform: scale(0.97);
  }

  /* Chosen: the card lifts and the corner badge fills - no frame around it. */
  &.is-selected {
    transform: translate3d(0, -4px, 0);
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.18);
  }

  &.is-unavailable {
    opacity: 0.45;
    cursor: default;
  }
`;

/** Top-right of the photo: the radio for single-select groups. Multi shows its count in the stepper. */
const Corner = styled.div`
  position: absolute;
  top: 9px;
  right: 9px;
  padding: 4px;
  border-radius: 999px;
  background: var(--eposSurface);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

/** Bottom-right of the photo: opens the description and allergens. Same pill as Corner. */
const InfoButton = styled.button`
  appearance: none;
  border: none;
  margin: 0;
  position: absolute;
  bottom: 9px;
  right: 9px;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  cursor: pointer;
  background: var(--eposSurface);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.15s ease-out;

  &.is-touching {
    transform: scale(0.9);
  }
`;

const Body = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px 16px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.2px;
`;

const Description = styled.div`
  font-size: 13px;
  line-height: 1.35;
  opacity: 0.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

/**
 * Delta price left, small stepper right (multi) - or the delta alone (single).
 * Wraps onto two rows when the card is narrow instead of clipping the "+".
 */
const Foot = styled.div`
  margin-top: auto;
  padding-top: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px;
`;

const Delta = styled.span`
  font-size: 17px;
  font-weight: 800;
  white-space: nowrap;
  opacity: 0.7;
  transition: opacity 0.15s ease-out;

  .is-selected & {
    opacity: 1;
  }
`;

/** Unavailable label centered over the greyed photo. */
const UnavailableBadge = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  white-space: nowrap;
  background: var(--colorBlueGray1200);
  color: var(--colorWhite);
`;

interface Props {
  item: EposMenuItem;
  /** Single-select shows a radio (tap toggles); multi shows a stepper (tap increments). */
  multiSelect: boolean;
  /** How many of this item are attached to the parent line. */
  quantity: number;
  /** Out of stock - greyed out, taps ignored. */
  unavailable: boolean;
  onIncrement: (item: EposMenuItem) => void;
  onDecrement: (item: EposMenuItem) => void;
}

const UpsellCard: FC<Props> = ({
  item,
  multiSelect,
  quantity,
  unavailable,
  onIncrement,
  onDecrement,
}) => {
  const { i18n } = useI18n();
  const currency = useCurrency();
  const logger = useLogger('epos-upsell-card');
  const selected = quantity > 0;
  /** Nothing to tell - no description, no allergens - means no info button at all. */
  const hasInfo = !!item.description || item.allergyTags.length > 0;

  /** The info modal is mounted on demand and lives in a portal, so the card only renders on open/close. */
  const [infoOpen, setInfoOpen] = useState(false);
  const handleOpenInfo = useCallback(() => {
    logger.info('Upsell info opened', { itemId: item.id });
    setInfoOpen(true);
  }, [item.id]);
  const handleCloseInfo = useCallback(() => setInfoOpen(false), []);

  /** The idle timeout (or start over) must not leave the modal up for the next shopper. */
  const sessionActive = useSessionActive();
  useEffect(() => {
    if (!sessionActive) {
      logger.info('Session ended - closing the upsell info modal', { itemId: item.id });
      setInfoOpen(false);
    }
  }, [sessionActive]);

  /** Single: a tap on a chosen card clears it. Multi: every tap adds one. */
  const handleTap = useCallback(() => {
    if (!multiSelect && selected) {
      onDecrement(item);
      return;
    }
    onIncrement(item);
  }, [multiSelect, selected, item, onIncrement, onDecrement]);

  const handleIncrement = useCallback(() => onIncrement(item), [onIncrement, item]);
  const handleDecrement = useCallback(() => onDecrement(item), [onDecrement, item]);

  const { touching, disabled, disabledToast, ...button } = useButton({
    onClick: handleTap,
    disabled: unavailable,
  });
  /** useButton stops propagation, so a tap here never reaches the card's own tap. */
  const {
    touching: infoTouching,
    disabled: infoD,
    disabledToast: infoT,
    ...infoButton
  } = useButton({ onClick: handleOpenInfo });

  const className = [
    touching && 'is-touching',
    selected && 'is-selected',
    unavailable && 'is-unavailable',
  ]
    .filter(Boolean)
    .join(' ');

  /** Free add-ons keep the plain formatted price - "+ 0 kr" would undersell them. */
  const delta = item.priceValue > 0 ? `+ ${currency.format(item.priceValue)}` : item.price;

  return (
    <Card
      {...button}
      role="button"
      className={className || undefined}
      data-testid="epos-upsell-card"
      data-item-id={item.id}>
      <ItemImage
        imageUrl={item.imageUrl}
        cdnWidth={CARD_IMAGE_WIDTH}
        borderRadius={0}
        grayscale={unavailable}>
        {unavailable && <UnavailableBadge>{i18n('Express.Item.Unavailable')}</UnavailableBadge>}
        {!multiSelect && !unavailable && (
          <Corner>
            <RadioButton checked={selected} />
          </Corner>
        )}
        {hasInfo && (
          <InfoButton
            {...infoButton}
            type="button"
            aria-label="info"
            data-testid="epos-upsell-info"
            className={infoTouching ? 'is-touching' : undefined}>
            <Icon src={infoIcon} color="var(--eposText)" width="18px" height="18px" />
          </InfoButton>
        )}
      </ItemImage>
      <Body>
        <Title>{item.title}</Title>
        {!!item.description && <Description>{item.description}</Description>}
        <Foot>
          <Delta>{delta}</Delta>
          {multiSelect && !unavailable && (
            <QuantityStepper
              size="small"
              quantity={quantity}
              onIncrement={handleIncrement}
              onDecrement={handleDecrement}
            />
          )}
        </Foot>
      </Body>
      {hasInfo && <UpsellInfoModal item={item} open={infoOpen} onClose={handleCloseInfo} />}
    </Card>
  );
};

export default memo(UpsellCard);
